import { useEffect, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { describeSync, sourceTime, type AutomaticSync } from '../lib/sourceSync';

export function AutomaticSyncStatus({
  automatic,
  configured,
}: {
  automatic?: AutomaticSync;
  configured: boolean;
}) {
  const view = describeSync(automatic, configured);
  return (
    <div className="form-note">
      <strong>{view.title}</strong>
      <p>
        {view.schedule}. {view.next}
      </p>
      <p>{view.reason}</p>
      {view.recovery && <p>{view.recovery}</p>}
    </div>
  );
}

type InventoryStatus = {
  provider?: string;
  source?: string;
  configured: boolean;
  missingConfiguration: string[];
  configurationError?: string | null;
  lastSync?:
    | string
    | {
        lastSync?: string;
        at?: string;
        completedAt?: string;
        total?: number;
        imported?: number;
        created?: number;
        updated?: number;
        unchanged?: number;
        catalog?: number;
        readyForMatching?: number;
        invalid?: { sourceRef: string; reason: string }[];
      }
    | null;
  lastError?: { at?: string; message?: string; code?: string } | null;
  automatic?: AutomaticSync;
};

export function InventorySyncStatus({
  refreshKey,
  onRefresh,
}: { refreshKey?: unknown; onRefresh?: () => Promise<void> } = {}) {
  const [status, setStatus] = useState<InventoryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api('/sources/status')
      .then((result) => {
        if (!active) return;
        if (!result.inventory) throw new Error('Inventory source status is unavailable.');
        setStatus(result.inventory);
      })
      .catch((e) => {
        if (active) {
          setStatus(null);
          setError(e.message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision, refreshKey]);
  const receipt = status?.lastSync && typeof status.lastSync === 'object' ? status.lastSync : null;
  const last =
    typeof status?.lastSync === 'string'
      ? status.lastSync
      : status?.lastSync?.lastSync || status?.lastSync?.at || status?.lastSync?.completedAt;
  return (
    <div className="form-note" aria-busy={loading}>
      <div className="ops-inline">
        <ShieldCheck size={17} />
        <strong>Indian Agency property inventory</strong>
        <button
          className="btn btn-ghost btn-sm"
          disabled={loading}
          onClick={async () => {
            try {
              await onRefresh?.();
              setRevision((value) => value + 1);
            } catch (e) {
              setError((e as Error).message);
            }
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Refresh status
        </button>
      </div>
      {loading && <p role="status">Checking saved inventory source status…</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {status && (
        <>
          <p>
            {status.configured
              ? 'Inventory settings are present. A successful source sync confirms an operation.'
              : 'Connect the Indian Agency admin source to bring its property listings into the CRM.'}
          </p>
          {!!status.missingConfiguration?.length && (
            <p>Missing settings: {status.missingConfiguration.join(', ')}.</p>
          )}
          {status.configurationError && <p className="form-error">{status.configurationError}</p>}
          <p>Last successful inventory sync: {sourceTime(last) || 'Not recorded'}.</p>
          {receipt?.total !== undefined && (
            <p>
              {receipt.imported ??
                (receipt.created || 0) + (receipt.updated || 0) + (receipt.unchanged || 0)}{' '}
              of {receipt.total} source properties imported.
              {receipt.catalog !== undefined &&
                ` ${receipt.catalog} catalog listings need unit details or verification.`}
              {receipt.readyForMatching !== undefined &&
                ` ${receipt.readyForMatching} ready for buyer matching at the last sync.`}
            </p>
          )}
          {!!receipt?.invalid?.length && (
            <div role="alert" className="form-error">
              <strong>{receipt.invalid.length} source properties could not be imported.</strong>
              <ul>
                {receipt.invalid.map((row, index) => (
                  <li key={`${row.sourceRef}-${index}`}>
                    {row.sourceRef}: {row.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {status.lastError && (
            <p className="form-error">
              Latest error
              {sourceTime(status.lastError.at) ? ` · ${sourceTime(status.lastError.at)}` : ''}:{' '}
              {status.lastError.message ||
                status.lastError.code ||
                'The last inventory sync did not complete.'}
            </p>
          )}
          <AutomaticSyncStatus automatic={status.automatic} configured={status.configured} />
        </>
      )}
    </div>
  );
}
